/**
 * Tema por tenant (plan 7.3.5).
 *
 * El tema llega dentro del bloque `checkout` del payment intent, es decir, viene de
 * configuracion editable en el backoffice. Por eso TODO valor se valida por regex
 * antes de tocar el DOM: si `tenant_gateway_configs` o la fila del tenant estuvieran
 * comprometidas, un valor como `red; background:url(https://evil/?c=` no debe poder
 * inyectar CSS. Lo que no valida se descarta en silencio y se usa el default de
 * `styles.css`.
 */
import type { TenantTheme } from '../types/api';

/** Solo hex de 6 digitos: descarta funciones CSS, `var(...)`, `expression(...)`, etc. */
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

/** Radios de 0px a 99px; nada de calc(), em, % ni unidades relativas al viewport. */
const RADIUS = /^\d{1,2}px$/;

/** Caracteres que romperian un `url(...)` o un atributo y habilitarian inyeccion. */
const UNSAFE_URL_CHARS = /["'()<>\\\s]/;

export const CSS_VAR_PRIMARY = '--apipay-color-primary';
export const CSS_VAR_RADIUS = '--apipay-radius';

/** Devuelve el color si es un hex de 6 digitos; si no, null. */
export function sanitizeColor(value: unknown): string | null {
  return typeof value === 'string' && HEX_COLOR.test(value) ? value : null;
}

/** Devuelve el radio si es `<n>px` con una o dos cifras; si no, null. */
export function sanitizeRadius(value: unknown): string | null {
  return typeof value === 'string' && RADIUS.test(value) ? value : null;
}

/**
 * Devuelve la URL del logo solo si es https absoluta y no contiene caracteres que
 * permitirian escapar de un atributo o de un `url(...)`. Cualquier otro esquema
 * (`javascript:`, `data:`, `http:`) se descarta.
 */
export function sanitizeLogoUrl(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0) {
    return null;
  }
  if (UNSAFE_URL_CHARS.test(value)) {
    return null;
  }
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return null;
  }
  return parsed.protocol === 'https:' ? parsed.toString() : null;
}

/**
 * Normaliza el objeto libre `checkout.theme` del contrato OpenAPI a la forma tipada
 * del widget, quedandose solo con los valores que superan la validacion. Acepta los
 * alias historicos `color_primary` y `accent_color` que usan los ejemplos del plan y
 * del contrato para la misma propiedad.
 */
export function sanitizeTenantTheme(raw: unknown): TenantTheme | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return undefined;
  }
  const source = raw as Record<string, unknown>;
  const theme: TenantTheme = {};

  const primary =
    sanitizeColor(source['primary_color']) ??
    sanitizeColor(source['color_primary']) ??
    sanitizeColor(source['accent_color']);
  if (primary !== null) {
    theme.primary_color = primary;
  }

  const radius = sanitizeRadius(source['radius']);
  if (radius !== null) {
    theme.radius = radius;
  }

  const logoUrl = sanitizeLogoUrl(source['logo_url']);
  if (logoUrl !== null) {
    theme.logo_url = logoUrl;
  }

  return theme;
}

/**
 * Escribe las CSS variables del tema en el elemento raiz. Se vuelve a validar aunque
 * el tema ya haya pasado por sanitizeTenantTheme(): defensa en profundidad, porque
 * esta funcion es publica dentro del widget y podria recibir un objeto no normalizado.
 */
export function applyTenantTheme(
  theme: TenantTheme | undefined,
  root: HTMLElement = document.documentElement,
): void {
  if (theme === undefined) {
    return;
  }

  const primary = sanitizeColor(theme.primary_color);
  if (primary !== null) {
    root.style.setProperty(CSS_VAR_PRIMARY, primary);
  }

  const radius = sanitizeRadius(theme.radius);
  if (radius !== null) {
    root.style.setProperty(CSS_VAR_RADIUS, radius);
  }
}
